import axios from 'axios';

// ─── Dynamic API base URL ─────────────────────────────────────────────────
// Priority:
//   1. REACT_APP_API_BASE_URL env var — manual override/toggle, set it in
//      Telecall_admin/.env.local to pin a specific backend URL
//   2. Derive from the browser's current hostname — opening the app via
//      localhost talks to localhost:5001; opening it via this PC's LAN IP
//      (e.g. http://192.168.1.11:3000, for a tester on the same Wi-Fi)
//      automatically talks to the backend on that same IP. No manual
//      switching needed; to force localhost regardless, use option 1 above.
//   3. Fallback to localhost for local development

// LOCAL: matches backend .env PORT=5001
const LOCAL_BACKEND_PORT = 5001;

// const getBaseUrl = () => {
//   if (process.env.REACT_APP_API_BASE_URL) return process.env.REACT_APP_API_BASE_URL;
//   const host = window.location.hostname;
//   if (host === 'localhost' || host === '127.0.0.1') {
//     return `http://localhost:${LOCAL_BACKEND_PORT}/api`;
//   }
//   return `http://${host}:${LOCAL_BACKEND_PORT}/api`;
// };

// const BASE_URL = getBaseUrl();
const BASE_URL = 'https://back.televalley.in/api';
// const BASE_URL = 'https://devback.televalley.in/api';
// const BASE_URL = 'http://192.168.1.10:5001/api';
// const BASE_URL = 'http://localhost:5001/api';


// Backend origin without the /api prefix. Used to resolve publicly-served
// upload URLs (e.g. /uploads/attachments/...) which are mounted at the
// server root, not under /api.
export const API_ORIGIN = BASE_URL.replace(/\/api\/?$/, '');

// Resolve an attachment/upload path to a full URL. Already-absolute URLs
// (http/https) are returned untouched.
export const filePublicUrl = (path) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  return `${API_ORIGIN}${path.startsWith('/') ? '' : '/'}${path}`;
};

// Create axios instance
const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token
api.interceptors.request.use(
  (config) => {
    // Check if this is a superadmin or channel-partner route
    const isSuperAdminRoute = config.url?.startsWith('/superadmin');
    const isPartnerRoute = config.url?.startsWith('/partner');

    // Each portal keeps its own token: superadmin_token, partner_auth_token,
    // or admin_auth_token for everything else (the regular org-admin panel).
    let token;
    if (isSuperAdminRoute) {
      token = localStorage.getItem('superadmin_token');
    } else if (isPartnerRoute) {
      token = localStorage.getItem('partner_auth_token');
    } else {
      token = localStorage.getItem('admin_auth_token');
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Increase timeout for uploads
    if (config.url?.includes('/upload')) {
      config.timeout = 5 * 60 * 1000; // 5 minutes
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Log all errors for debugging
    console.error('API Error:', {
      url: error.config?.url,
      status: error.response?.status,
      message: error.response?.data?.message || error.message
    });

    // Handle 401 Unauthorized - but don't immediately redirect
    if (error.response?.status === 401) {
      const isLoginPage = window.location.pathname === '/login';
      const isSuperAdminLoginPage = window.location.pathname === '/superadmin/login';
      const isPartnerLoginPage = window.location.pathname === '/partner/login';

      if (!isLoginPage && !isSuperAdminLoginPage && !isPartnerLoginPage) {
        console.warn('Auth token expired or invalid');

        // Check which portal this request belonged to
        const isSuperAdminRoute = error.config?.url?.startsWith('/superadmin');
        const isPartnerRoute = error.config?.url?.startsWith('/partner');

        if (isSuperAdminRoute) {
          // Clear superadmin storage
          localStorage.removeItem('superadmin_token');
          localStorage.removeItem('superadmin_user');
        } else if (isPartnerRoute) {
          // Clear channel-partner storage
          localStorage.removeItem('partner_auth_token');
          localStorage.removeItem('partner_user');
        } else {
          // Clear admin storage
          localStorage.removeItem('admin_auth_token');
          localStorage.removeItem('admin_user_data');
        }
        // Don't redirect - let the app handle it naturally
        // The AuthContext/SuperAdminContext will detect missing token and show login
      }
    }

    // Handle 403 with subscriptionExpired - dispatch event for OrgContext to pick up
    if (error.response?.status === 403 && error.response?.data?.subscriptionExpired) {
      const event = new CustomEvent('subscriptionExpired', {
        detail: {
          reason: error.response.data.reason,
          message: error.response.data.message,
          trialEndsAt: error.response.data.trialEndsAt,
          subscriptionEndsAt: error.response.data.subscriptionEndsAt,
        }
      });
      window.dispatchEvent(event);
    }
    return Promise.reject(error);
  }
);

// ========================================
// AUTH API
// ========================================

// Channel Partner's own portal login (separate from org-admin and superadmin auth)
export const channelPartnerAuthAPI = {
  login: (email, password) => api.post('/partner/auth/login', { email, password }),
  me: () => api.get('/partner/auth/me'),
  logout: () => api.post('/partner/auth/logout'),
  changePassword: (data) => api.post('/partner/auth/change-password', data),
  updatePayout: (data) => api.patch('/partner/auth/payout', data),
  getCommissions: (params) => api.get('/partner/commissions', { params }),
  getOrganizations: (params) => api.get('/partner/organizations', { params }),
  getNotices: () => api.get('/partner/notices'),
  // Invite a prospective lead by email — sends them the partner's referral link
  sendInvite: (email) => api.post('/partner/auth/invite', { email }),

  // Support tickets — a fully separate ticket system from org support
  // tickets (own models/routes, see routes/channelPartnerPortalRoutes.js)
  getSupportTickets: () => api.get('/partner/support-tickets'),
  getSupportTicket: (id) => api.get(`/partner/support-tickets/${id}`),
  getSupportCategories: () => api.get('/partner/support-categories'),
  createSupportTicket: (data) => api.post('/partner/support-tickets', data),
  // Same endpoint, multipart when files are attached at creation
  createSupportTicketWithFiles: (formData) =>
    api.post('/partner/support-tickets', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  replySupportTicket: (id, data) => api.post(`/partner/support-tickets/${id}/activities`, data),
  replySupportTicketWithFiles: (id, formData) =>
    api.post(`/partner/support-tickets/${id}/activities`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  // Meeting requests — partner asks the TeleValley team to meet a lead directly
  getMeetingRequests: () => api.get('/partner/meeting-requests'),
  createMeetingRequest: (data) => api.post('/partner/meeting-requests', data),

  // KYC — shown as a form in the portal (PartnerLayout.jsx) when kycStatus is
  // 'not_submitted'; flips to 'pending' on success (see routes/channelPartnerAuthRoutes.js)
  submitKyc: (formData) =>
    api.post('/partner/auth/kyc', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
};

// Account Switcher (docs/ACCOUNT_SWITCHER_PLAN.md) — one email, multiple orgs
export const accountSwitchAPI = {
  myOrganizations: () => api.get('/auth/my-organizations'),
  // Linked Workspaces (docs/LINKED_WORKSPACES_PLAN.md) — DISABLED for now, feature
  // planned for later. Commented out, not removed, per explicit request.
  // createWorkspace: (name) => api.post('/organizations/me/workspaces', { name }),
};

export const authAPI = {
  login: (identifier, password) =>
    api.post('/auth/login', { email: identifier, password }),

  logout: () => api.post('/auth/logout'),

  getProfile: () => api.get('/auth/me'),

  updateProfile: (data) => api.put('/auth/profile', data),

  changePassword: (currentPassword, newPassword) =>
    api.put('/auth/password', { currentPassword, newPassword }),

  // ── Forgot password — OTP sent to the account's email ────────────────────
  // Step 1: request OTP for the email
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),

  // Step 2: verify the OTP → returns a short-lived resetToken
  verifyOtp: (email, otp) => api.post('/auth/verify-otp', { email, otp }),

  // Step 2b: resend the OTP (rate-limited server-side)
  resendOtp: (email) => api.post('/auth/resend-otp', { email }),

  // Step 3: set the new password using the verified resetToken
  resetPassword: (resetToken, newPassword, confirmPassword) =>
    api.post('/auth/reset-password', { resetToken, newPassword, confirmPassword }),
};

// ========================================
// USERS API
// ========================================

export const usersAPI = {
  getAll: (params) => api.get('/users', { params }),

  getById: (id) => api.get(`/users/${id}`),

  create: (data) => api.post('/users', data),

  update: (id, data) => api.put(`/users/${id}`, data),

  // data = { transferToUserId } — required when the user still owns data
  // (leads, call logs, follow-ups, etc.); the backend refuses the delete otherwise.
  delete: (id, data) => api.delete(`/users/${id}`, data ? { data } : undefined),

  // What the user owns + other users it could be transferred to (for the
  // delete-with-transfer dialog).
  getDataSummary: (id) => api.get(`/users/${id}/data-summary`),

  bulkUpdate: (data) => api.post('/users/bulk-update', data),

  getPerformance: (id, params) => api.get(`/users/${id}/performance`, { params }),

  // Send OTP to verify a new user's email (admin action)
  sendEmailOTP: (email) => api.post('/users/send-email-otp', { email }),

  // Verify OTP for new user's email (admin action)
  verifyEmailOTP: (email, otp) => api.post('/users/verify-email-otp', { email, otp }),
};

// ========================================
// LEADS API
// ========================================

export const leadsAPI = {
  getAll: (params) => api.get('/telecalling/leads', { params }),

  getMy: (params) => api.get('/telecalling/leads/my', { params }),

  getById: (id) => api.get(`/telecalling/leads/${id}`),

  create: (data) => api.post('/telecalling/leads', data),

  bulkCreate: (data) => api.post('/telecalling/leads/bulk', data),

  update: (id, data) => api.put(`/telecalling/leads/${id}`, data),

  delete: (id) => api.delete(`/telecalling/leads/${id}`),

  bulkDelete: (ids) => api.post('/telecalling/leads/bulk-delete', { ids }),

  bulkUpdate: (data) => api.post('/telecalling/leads/bulk-update', data),

  assign: (leadIds, userId) =>
    api.post('/telecalling/leads/assign', { leadIds, assignedTo: userId }),

  bulkAssign: (data) => api.post('/telecalling/leads/assign', data),

  // Assign leads by filters (count-based)
  assignByFilters: (data) => api.post('/telecalling/leads/assign-by-filters', data),

  getCounts: (params) => api.get('/telecalling/leads/counts', { params }),

  getStatusCounts: (params) => api.get('/telecalling/leads/status-counts', { params }),

  getByCategory: (category, params) =>
    api.get('/telecalling/leads', { params: { category, ...params } }),

  // Import leads with optional equal distribution
  importLeads: (data) => api.post('/telecalling/leads/import', data),

  exportCSV: (params) =>
    api.get('/telecalling/leads/export', { params, responseType: 'blob' }),

  // Get all unique sources
  getSources: () => api.get('/telecalling/leads/sources'),

  // Add note to a lead (with optional file attachments)
  addNote: (id, data) => {
    if (data instanceof FormData) {
      return api.post(`/telecalling/leads/${id}/notes`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
    return api.post(`/telecalling/leads/${id}/notes`, data);
  },

  // Get unified activity timeline for a lead
  getActivity: (id, params) => api.get(`/telecalling/leads/${id}/activity`, { params }),
};

// ========================================
// SMART LISTS (LEAD SEGMENTS) API
// Dynamic lead groups saved from filter combinations for targeted outreach
// ========================================

export const smartListsAPI = {
  getAll: (params) => api.get('/lead-segments', { params }),

  getById: (id) => api.get(`/lead-segments/${id}`),

  create: (data) => api.post('/lead-segments', data),

  update: (id, data) => api.put(`/lead-segments/${id}`, data),

  delete: (id) => api.delete(`/lead-segments/${id}`),

  // Preview the live match count + sample leads for unsaved filters
  preview: (filters) => api.post('/lead-segments/preview', { filters }),

  // Paginated leads matching a saved smart list
  getLeads: (id, params) => api.get(`/lead-segments/${id}/leads`, { params }),
};

// ========================================
// RE-ENGAGEMENT (RECHURN) API
// Identifies inactive/lost leads and manages win-back campaigns
// ========================================

export const reEngagementAPI = {
  // Bucket counts: lost, cold, inactive30/60/90, neverContacted, overdue
  getSummary: () => api.get('/re-engagement/summary'),

  // Paginated leads in a bucket (?bucket=lost|cold|inactive30|...)
  getBucketLeads: (bucket, params) => api.get('/re-engagement/leads', { params: { bucket, ...params } }),

  getCampaigns: () => api.get('/re-engagement/campaigns'),

  createCampaign: (data) => api.post('/re-engagement/campaigns', data),

  updateCampaign: (id, data) => api.put(`/re-engagement/campaigns/${id}`, data),

  setCampaignStatus: (id, status) => api.put(`/re-engagement/campaigns/${id}/status`, { status }),

  // Run campaign — generates re-engagement follow-up tasks for targeted leads
  runCampaign: (id, assignTo) => api.post(`/re-engagement/campaigns/${id}/run`, { assignTo }),

  deleteCampaign: (id) => api.delete(`/re-engagement/campaigns/${id}`),
};

// ========================================
// TERRITORIES API
// Geographic lead territories with area-specific agents
// ========================================

export const territoriesAPI = {
  getAll: () => api.get('/territories'),

  getById: (id) => api.get(`/territories/${id}`),

  create: (data) => api.post('/territories', data),

  update: (id, data) => api.put(`/territories/${id}`, data),

  delete: (id) => api.delete(`/territories/${id}`),

  // Paginated leads inside a territory (?assignment=unassigned|assigned)
  getLeads: (id, params) => api.get(`/territories/${id}/leads`, { params }),

  // Distribute the territory's leads round-robin across its agents
  assignLeads: (id, includeAssigned) => api.post(`/territories/${id}/assign`, { includeAssigned }),

  // Resolve a 6-digit Indian pincode to city + state (India Post data)
  pincodeLookup: (pincode) => api.get(`/territories/pincode-lookup/${pincode}`),
};

// ========================================
// CALL LOGS API
// ========================================

export const callLogsAPI = {
  getAll: (params) => api.get('/telecalling/call-logs', { params }),

  getById: (id) => api.get(`/telecalling/call-logs/${id}`),

  create: (data) => api.post('/telecalling/call-logs', data),

  getStatistics: (params) => api.get('/telecalling/call-logs/statistics', { params }),

  getUsersWithPhones: () => api.get('/telecalling/call-logs/users-with-phones'),

  getByUser: (userId, params) =>
    api.get('/telecalling/call-logs', { params: { assignedTo: userId, ...params } }),
};

// ========================================
// RECORDINGS API (Server Storage)
// RECORDINGS API (Cloudinary)
// ========================================

export const recordingsAPI = {
  // Upload recording file
  upload: (formData) => api.post('/recordings/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),

  // Get all recordings (Admin)
  getAll: (params) => api.get('/recordings', { params }),

  // Get single recording
  getById: (id) => api.get(`/recordings/${id}`),

  // Get recording file URL
  getFileUrl: (id) => `${BASE_URL}/recordings/file/${id}`,

  // Delete recording
  delete: (id) => api.delete(`/recordings/${id}`),
};

// ========================================
// CALL ANALYSIS API
// ========================================

export const callAnalysisAPI = {
  // Upload recording + start analysis (multipart); '/upload' suffix triggers
  // the axios interceptor's 5-minute upload timeout
  upload: (formData) => api.post('/call-analysis/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),

  // List analyses with filters/pagination
  list: (params) => api.get('/call-analysis', { params }),

  // All recordings from the Recordings module, joined with their analysis status
  listRecordings: (params) => api.get('/call-analysis/recordings', { params }),

  // Single analysis with full transcript
  getById: (id) => api.get(`/call-analysis/${id}`),

  // Lightweight status for polling
  getStatus: (id) => api.get(`/call-analysis/${id}/status`),

  // Analyze a recording that already exists in the Recordings module
  analyzeExisting: (data) => api.post('/call-analysis/analyze-existing', data, { timeout: 60000 }),

  // Bulk: queue multiple existing recordings for analysis (sequential queue)
  analyzeMany: (data) => api.post('/call-analysis/analyze-many', data, { timeout: 120000 }),

  // Backfill: queue analyses for ALL recordings that don't have one yet
  // (covers old recordings uploaded before auto-analysis existed)
  backfill: (data) => api.post('/call-analysis/backfill', data, { timeout: 120000 }),

  // Re-run a failed/completed analysis
  retry: (id) => api.post(`/call-analysis/${id}/retry`),

  // Re-run ONLY the marking (percentage) on the stored transcript —
  // adds/refreshes marks without re-transcribing
  rescore: (id) => api.post(`/call-analysis/${id}/rescore`),

  // Delete analysis (soft)
  remove: (id) => api.delete(`/call-analysis/${id}`),

  // Which providers are configured (Deepgram key, LLM availability)
  getProviders: () => api.get('/call-analysis/providers'),
};

// ========================================
// TASKS API
// ========================================

export const tasksAPI = {
  getAll: (params) => api.get('/telecalling/tasks', { params }),

  getById: (id) => api.get(`/telecalling/tasks/${id}`),

  create: (data) => api.post('/telecalling/tasks', data),

  update: (id, data) => api.put(`/telecalling/tasks/${id}`, data),

  complete: (id, notes) => api.put(`/telecalling/tasks/${id}/complete`, { notes }),

  delete: (id) => api.delete(`/telecalling/tasks/${id}`),
};

// ========================================
// DASHBOARD API
// ========================================

export const dashboardAPI = {
  getStats: (params) => api.get('/telecalling/dashboard', { params }),

  getDailyReport: (date) => api.get('/telecalling/reports/daily', { params: { date } }),

  getWeeklyReport: (startDate, endDate) =>
    api.get('/telecalling/reports/weekly', { params: { startDate, endDate } }),

  getMonthlyReport: (month, year) =>
    api.get('/telecalling/reports/monthly', { params: { month, year } }),

  getLeaderboard: (params) => api.get('/telecalling/reports/leaderboard', { params }),

  // CEO Dashboard alerts
  getAlerts: () => api.get('/telecalling/dashboard/alerts'),

  // Pending actions overview
  getPendingActions: (params) => api.get('/telecalling/dashboard/pending-actions', { params }),
};

// ========================================
// REPORTS API
// ========================================

export const reportsAPI = {
  getDailyCalls: (params) => api.get('/telecalling/reports/daily-calls', { params }),

  // Personal report for the logged-in user (telecaller User Panel)
  getMyReport: (params) => api.get('/telecalling/reports/my-report', { params }),

  getWeeklyTrends: () => api.get('/telecalling/reports/weekly-trends'),

  getLeadSources: (params) => api.get('/telecalling/reports/lead-sources', { params }),

  // Product-wise performance
  getProductPerformance: (params) => api.get('/telecalling/reports/product-performance', { params }),

  // Follow-up statistics
  getFollowUpStats: (params) => api.get('/telecalling/reports/follow-up-stats', { params }),

  // Call status distribution
  getCallStatusDistribution: (params) => api.get('/telecalling/reports/call-status-distribution', { params }),

  // Follow-up status distribution
  getFollowUpStatusDistribution: (params) => api.get('/telecalling/reports/follow-up-status-distribution', { params }),

  // Lost reason analysis
  getLostReasonAnalysis: (params) => api.get('/telecalling/reports/lost-reason-analysis', { params }),

  // Lead Response-Time Analytics (auto-generated from leads & call logs)
  getLeadResponseTime: (params) => api.get('/telecalling/reports/lead-response-time', { params }),

  // Lead Ageing Report — leads grouped by days in system / in current stage
  getLeadAgeing: (params) => api.get('/telecalling/reports/lead-ageing', { params }),

  // Lead & call details table
  getLeadCallDetails: (params) => api.get('/telecalling/reports/lead-call-details', { params }),

  // Team performance
  getTeamPerformance: (params) => api.get('/telecalling/reports/team-performance', { params }),

  // Team summary
  getTeamSummary: (params) => api.get('/telecalling/reports/team-summary', { params }),

  // Daily Work Reports
  getDailyWorkReport: (params) => api.get('/telecalling/reports/daily-work', { params }),

  getUserDailyReport: (userId, params) => api.get(`/telecalling/reports/daily-work/${userId}`, { params }),

  exportDailyWork: (params) => api.get('/telecalling/reports/daily-work-export', { params }),

  // Excel Report Exports
  exportTeamPerformance: (params) => api.get('/telecalling/reports/export/team-performance', { params, responseType: 'blob' }),
  // Visits & productivity (Reports & Analytics → Visits tab)
  getVisitsReport: (params) => api.get('/telecalling/reports/visits', { params }),
  exportVisits: (params) => api.get('/telecalling/reports/export/visits', { params, responseType: 'blob' }),

  exportCallLogs: (params) => api.get('/telecalling/reports/export/call-logs', { params, responseType: 'blob' }),

  exportLeads: (params) => api.get('/telecalling/reports/export/leads', { params, responseType: 'blob' }),

  // Custom Report Builder Export
  exportCustom: (config) => api.post('/telecalling/reports/export/custom', config, { responseType: 'blob' }),

  // Preview data for the Custom Report Builder — serves both "records"
  // (first 50 rows) and "day" (one summary row per day) modes. Dedicated
  // endpoint because the generic list APIs ignore date params / scope
  // follow-ups to the logged-in user only.
  previewCustom: (payload) => api.post('/telecalling/reports/custom-preview', payload),
};

// ========================================
// FOLLOW-UPS API
// ========================================

export const followUpsAPI = {
  // Get all follow-ups (admin view with filters)
  getAll: (params) => api.get('/telecalling/follow-ups/admin', { params }),

  // Create a new follow-up
  create: (data) => api.post('/telecalling/follow-ups', data),

  // Update follow-up status
  update: (id, data) => api.put(`/telecalling/follow-ups/${id}`, data),

  // Delete follow-up
  delete: (id) => api.delete(`/telecalling/follow-ups/${id}`),

  // Get escalated (overdue) follow-ups for managers
  getEscalated: (params) => api.get('/telecalling/follow-ups/escalated', { params }),

  // Manually escalate a follow-up
  escalate: (id) => api.patch(`/telecalling/follow-ups/${id}/escalate`),
};

// ========================================
// CALENDAR API (Settings → Calendar)
// ========================================

export const calendarAPI = {
  // Google Calendar ID + secret ICS feed URL for this org
  getSettings: () => api.get('/telecalling/calendar-settings'),

  // Save the org's Google Calendar ID (embedded in the panel)
  updateSettings: (googleCalendarId) =>
    api.patch('/telecalling/calendar-settings', { googleCalendarId }),

  // Scheduled follow-ups + activities in a date range (calendar view)
  // params: { start: 'YYYY-MM-DD', end: 'YYYY-MM-DD' }
  getEvents: (params) => api.get('/telecalling/calendar-events', { params }),
};

// ========================================
// GOOGLE MEET API (Calendar → Google Meet Integration)
// ========================================

export const meetingsAPI = {
  // List online meetings for the organization (upcoming by default)
  list: (params) => api.get('/telecalling/meetings', { params }),

  // Schedule a new online meeting
  create: (data) => api.post('/telecalling/meetings', data),

  // Update or cancel a meeting (status: scheduled/completed/cancelled)
  update: (id, data) => api.patch(`/telecalling/meetings/${id}`, data),
};

// ========================================
// VISITS & MEETINGS API (site visits / meetings with leads)
// ========================================

export const visitsAPI = {
  list: (params) => api.get('/telecalling/visits', { params }),
  calendar: (params) => api.get('/telecalling/visits/calendar', { params }),
  getById: (id) => api.get(`/telecalling/visits/${id}`),
  pendingCount: () => api.get('/telecalling/visits/pending-count'),
  checkSlot: (params) => api.get('/telecalling/visits/check-slot', { params }),
  create: (data) => api.post('/telecalling/visits', data),
  update: (id, data) => api.put(`/telecalling/visits/${id}`, data),
  approve: (id) => api.post(`/telecalling/visits/${id}/approve`),
  reject: (id, reason) => api.post(`/telecalling/visits/${id}/reject`, { reason }),
  cancel: (id, reason) => api.post(`/telecalling/visits/${id}/cancel`, { reason }),
  reschedule: (id, data) => api.post(`/telecalling/visits/${id}/reschedule`, data),
  getSettings: () => api.get('/telecalling/visits/settings'),
  updateSettings: (data) => api.put('/telecalling/visits/settings', data),
  // Configurable "How did the visit go?" outcomes
  getOutcomes: (params) => api.get('/telecalling/visits/outcomes', { params }),
  createOutcome: (data) => api.post('/telecalling/visits/outcomes', data),
  updateOutcome: (key, data) => api.put(`/telecalling/visits/outcomes/${encodeURIComponent(key)}`, data),
  deleteOutcome: (key) => api.delete(`/telecalling/visits/outcomes/${encodeURIComponent(key)}`),
  reorderOutcomes: (keys) => api.put('/telecalling/visits/outcomes/reorder', { keys }),
  resetOutcomes: () => api.post('/telecalling/visits/outcomes/reset'),
};

// ========================================
// ATTENDANCE API
// ========================================

export const attendanceAPI = {
  // Get all attendance records
  getAll: (params) => api.get('/telecalling/attendance/all', { params }),

  // Get by user
  getByUser: (userId, params) =>
    api.get('/telecalling/attendance/all', { params: { userId, ...params } }),

  // Get statistics
  getStats: (params) => api.get('/telecalling/attendance/stats', { params }),

  // Get user-wise report
  getReport: (params) => api.get('/telecalling/attendance/report', { params }),

  // Export report
  exportReport: (params) =>
    api.get('/telecalling/attendance/export', { params }),

  // Check in
  checkIn: (data) => api.post('/telecalling/attendance/checkin', data),

  // Check out
  checkOut: (data) => api.post('/telecalling/attendance/checkout', data),

  // Get today's attendance
  getToday: () => api.get('/telecalling/attendance/today'),

  // Get attendance history
  getHistory: (params) => api.get('/telecalling/attendance/history', { params }),

  // ========================================
  // ATTENDANCE MANAGEMENT LOGGED (Complete Report)
  // One API with ALL check-in / check-out details of every
  // session + working hours, status, lead allocation & summary
  // Params: date | startDate+endDate, userId, status, isActive, page, limit
  // ========================================
  getManagementLogged: (params) =>
    api.get('/telecalling/attendance/management-logged', { params }),

  // ========================================
  // EMPLOYEE ACTIVITY TIMELINE
  // Chronological history of an employee's calls, follow-ups,
  // messages (WhatsApp/SMS/Email) and CRM actions
  // Params: userId (required), date | startDate+endDate, types, page, limit
  // ========================================
  getActivityTimeline: (params) =>
    api.get('/telecalling/attendance/activity-timeline', { params }),

  // ========================================
  // LOGIN DURATION TRACKING (Reports)
  // Time each agent remains logged into the system
  // ========================================

  // Get per-agent login duration report
  getLoginDuration: (params) =>
    api.get('/telecalling/attendance/login-duration', { params }),

  // Get individual login/logout sessions for one agent
  getLoginDurationSessions: (params) =>
    api.get('/telecalling/attendance/login-duration/sessions', { params }),
};

// ========================================
// ALLOCATION API
// ========================================

export const allocationAPI = {
  // Get my allocation for today (telecaller)
  getMy: () => api.get('/telecalling/allocations/my'),

  // Get allocation report (admin/manager)
  getReport: (params) => api.get('/telecalling/allocations/report', { params }),

  // Get available unassigned leads count
  getAvailable: () => api.get('/telecalling/allocations/available'),

  // Manual allocate to user (admin/manager)
  allocate: (userId, limit) => api.post('/telecalling/allocations/allocate', { userId, limit }),

  // Batch allocate to all telecallers (admin only)
  batchAllocate: (limit) => api.post('/telecalling/allocations/batch', { limit }),

  // Reallocate pending leads (admin only)
  reallocate: (days, userId) => api.post('/telecalling/allocations/reallocate', { days, userId }),
};

// ========================================
// UPLOAD API
// ========================================

export const uploadAPI = {
  uploadFile: (formData) =>
    api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
};

// ========================================
// WHATSAPP TEMPLATES API
// ========================================

export const whatsappTemplatesAPI = {
  // Get all templates (Admin)
  getAll: (params) => api.get('/whatsapp-templates', { params }),

  // Get active templates (For Mobile/Telecaller)
  getActive: () => api.get('/whatsapp-templates/active'),

  // Get single template
  getById: (id) => api.get(`/whatsapp-templates/${id}`),

  // Create template
  create: (data) => api.post('/whatsapp-templates', data),

  // Update template
  update: (id, data) => api.put(`/whatsapp-templates/${id}`, data),

  // Delete template
  delete: (id) => api.delete(`/whatsapp-templates/${id}`),

  // Toggle template status
  toggle: (id) => api.patch(`/whatsapp-templates/${id}/toggle`),
};

// ========================================
// EMAIL TEMPLATES API
// ========================================

export const emailTemplatesAPI = {
  // Get all templates (Admin)
  getAll: (params) => api.get('/email-templates', { params }),

  // Get active templates (For Mobile/Telecaller)
  getActive: () => api.get('/email-templates/active'),

  // Get single template
  getById: (id) => api.get(`/email-templates/${id}`),

  // Create template
  create: (data) => api.post('/email-templates', data),

  // Update template
  update: (id, data) => api.put(`/email-templates/${id}`, data),

  // Delete template
  delete: (id) => api.delete(`/email-templates/${id}`),

  // Toggle template status
  toggle: (id) => api.patch(`/email-templates/${id}/toggle`),

  // Send an email template to a lead
  send: (id, data) => api.post(`/email-templates/${id}/send`, data),

  // Send a custom (non-template) email to a lead
  sendCustom: (data) => api.post('/emails/send', data),
};

// ========================================
// SMS TEMPLATES API
// ========================================

export const smsTemplatesAPI = {
  // Get all templates (Admin)
  getAll: (params) => api.get('/sms-templates', { params }),

  // Get active templates (For Mobile/Telecaller)
  getActive: () => api.get('/sms-templates/active'),

  // Get single template
  getById: (id) => api.get(`/sms-templates/${id}`),

  // Create template
  create: (data) => api.post('/sms-templates', data),

  // Update template
  update: (id, data) => api.put(`/sms-templates/${id}`, data),

  // Delete template
  delete: (id) => api.delete(`/sms-templates/${id}`),

  // Toggle template status
  toggle: (id) => api.patch(`/sms-templates/${id}/toggle`),
};

// ========================================
// SUPPORT / TICKET MANAGEMENT API
// ========================================

export const supportAPI = {
  // Categories
  getCategories: () => api.get('/support/categories'),
  createCategory: (data) => api.post('/support/categories', data),
  updateCategory: (id, data) => api.put(`/support/categories/${id}`, data),
  toggleCategory: (id) => api.patch(`/support/categories/${id}/toggle`),
  deleteCategory: (id) => api.delete(`/support/categories/${id}`),

  // Stages (workflow)
  getStages: () => api.get('/support/stages'),
  createStage: (data) => api.post('/support/stages', data),
  updateStage: (id, data) => api.put(`/support/stages/${id}`, data),
  reorderStages: (order) => api.put('/support/stages/reorder', { order }),
  toggleStage: (id) => api.patch(`/support/stages/${id}/toggle`),
  deleteStage: (id) => api.delete(`/support/stages/${id}`),

  // SLA policies (response/resolution time limits)
  getSLA: () => api.get('/support/sla'),
  updateSLA: (policies) => api.put('/support/sla', { policies }),

  // Tickets
  getTickets: (params) => api.get('/support/tickets', { params }),
  getTicket: (id) => api.get(`/support/tickets/${id}`),
  createTicket: (data) => api.post('/support/tickets', data),
  // Same endpoint, multipart when files are attached at creation
  createTicketWithFiles: (formData) =>
    api.post('/support/tickets', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  updateTicket: (id, data) => api.patch(`/support/tickets/${id}`, data),
  changeStage: (id, stageId, reason) => api.patch(`/support/tickets/${id}/stage`, { stageId, reason }),
  toggleTicketActive: (id, isActive) => api.patch(`/support/tickets/${id}/active`, { isActive }),
  addActivity: (id, data) => api.post(`/support/tickets/${id}/activities`, data),
  // Same endpoint, multipart when a reply is sent together with files
  // (attach-then-send) instead of as a separate upload action
  addActivityWithFiles: (id, formData) =>
    api.post(`/support/tickets/${id}/activities`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  // Attach files (screenshots, documents, images, …) to a ticket
  uploadTicketAttachments: (id, formData) =>
    api.post(`/support/tickets/${id}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  linkTickets: (id, targetTicketId) => api.post(`/support/tickets/${id}/link`, { targetTicketId }),
  mergeTicket: (id, duplicateTicketId) => api.post(`/support/tickets/${id}/merge`, { duplicateTicketId }),
  unlinkTicket: (id, targetTicketId) => api.delete(`/support/tickets/${id}/link/${targetTicketId}`),
  unmergeTicket: (id, duplicateTicketId) => api.delete(`/support/tickets/${id}/merge/${duplicateTicketId}`),

  // Public (no auth): customer status check by ticket number
  publicStatus: (ticketNumber) =>
    publicSupportAPI.get(`/support/public/status/${ticketNumber}`),
};

// Standalone public API instance (no auth header) for the customer status page
export const publicSupportAPI = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
});

// SuperAdmin side of the Channel Partner ticket system — a fully separate
// system from supportAPI/org tickets above (own models/routes, see
// routes/partnerTicketAdminRoutes.js). Mirrors supportAPI's shape, minus
// link/merge (not duplicated for partner tickets — see plan).
export const partnerTicketAdminAPI = {
  // Categories
  getCategories: () => api.get('/superadmin/partner-tickets/categories'),
  createCategory: (data) => api.post('/superadmin/partner-tickets/categories', data),
  updateCategory: (id, data) => api.put(`/superadmin/partner-tickets/categories/${id}`, data),
  toggleCategory: (id) => api.patch(`/superadmin/partner-tickets/categories/${id}/toggle`),
  deleteCategory: (id) => api.delete(`/superadmin/partner-tickets/categories/${id}`),

  // Stages (workflow)
  getStages: () => api.get('/superadmin/partner-tickets/stages'),
  createStage: (data) => api.post('/superadmin/partner-tickets/stages', data),
  updateStage: (id, data) => api.put(`/superadmin/partner-tickets/stages/${id}`, data),
  reorderStages: (order) => api.put('/superadmin/partner-tickets/stages/reorder', { order }),
  toggleStage: (id) => api.patch(`/superadmin/partner-tickets/stages/${id}/toggle`),
  deleteStage: (id) => api.delete(`/superadmin/partner-tickets/stages/${id}`),

  // Tickets
  getTickets: (params) => api.get('/superadmin/partner-tickets/tickets', { params }),
  getTicketStats: () => api.get('/superadmin/partner-tickets/tickets/stats'),
  getTicket: (id) => api.get(`/superadmin/partner-tickets/tickets/${id}`),
  updateTicket: (id, data) => api.patch(`/superadmin/partner-tickets/tickets/${id}`, data),
  followupTicket: (id, data) => api.post(`/superadmin/partner-tickets/tickets/${id}/followup`, data),
  followupTicketWithFiles: (id, formData) =>
    api.post(`/superadmin/partner-tickets/tickets/${id}/followup`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  uploadTicketAttachments: (id, formData) =>
    api.post(`/superadmin/partner-tickets/tickets/${id}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
};

// Resources CMS — blog posts, guides, videos, documentation pages. Managed
// here, rendered publicly by the landing-page app via GET /api/public/<type>.
export const blogAdminAPI = {
  getAll: () => api.get('/superadmin/blog'),
  create: (formData) => api.post('/superadmin/blog', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, formData) => api.put(`/superadmin/blog/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  toggle: (id) => api.patch(`/superadmin/blog/${id}/toggle`),
  delete: (id) => api.delete(`/superadmin/blog/${id}`),
  reorder: (order) => api.put('/superadmin/blog/reorder', { order }),
};

export const otherProductAdminAPI = {
  getAll: () => api.get('/superadmin/other-products'),
  create: (data) => api.post('/superadmin/other-products', data),
  update: (id, data) => api.put(`/superadmin/other-products/${id}`, data),
  toggle: (id) => api.patch(`/superadmin/other-products/${id}/toggle`),
  delete: (id) => api.delete(`/superadmin/other-products/${id}`),
  reorder: (order) => api.put('/superadmin/other-products/reorder', { order }),
};

export const videoAdminAPI = {
  getAll: () => api.get('/superadmin/videos'),
  create: (formData) => api.post('/superadmin/videos', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, formData) => api.put(`/superadmin/videos/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  toggle: (id) => api.patch(`/superadmin/videos/${id}/toggle`),
  delete: (id) => api.delete(`/superadmin/videos/${id}`),
  reorder: (order) => api.put('/superadmin/videos/reorder', { order }),
};

// ========================================
// PUBLIC API (No Auth Required)
// ========================================

export const publicAPI = {
  // Get all active plans
  getPlans: () => api.get('/public/plans'),

  // Get plan by slug
  getPlanBySlug: (slug) => api.get(`/public/plans/${slug}`),

  // Check slug availability
  checkSlug: (slug) => api.get(`/public/check-slug/${slug}`),

  // Check email availability
  checkEmail: (email) => api.get(`/public/check-email/${email}`),

  // Country list + required/optional KYC document types — used by the
  // partner portal's post-login KYC form (PartnerLayout.jsx)
  getKycRequirements: () => api.get('/public/kyc-requirements'),

  // Send OTP for email verification during registration
  sendRegistrationOTP: (email) => api.post('/public/send-registration-otp', { email }),

  // Verify OTP for registration
  verifyRegistrationOTP: (email, otp) => api.post('/public/verify-registration-otp', { email, otp }),

  // Register organization
  register: (data) => api.post('/public/register', data),

  // Health check
  healthCheck: () => api.get('/public/health'),

  // Public payment: create order for new signup
  createPaymentOrder: (data) => api.post('/public/payments/create-order', data),

  // Public payment: verify and register
  verifyPayment: (data) => api.post('/public/payments/verify', data),
};

// ========================================
// SUPER ADMIN API
// ========================================

export const superAdminAPI = {
  // Organizations
  getOrganizations: (params) => api.get('/superadmin/organizations', { params }),
  getOrganization: (id) => api.get(`/superadmin/organizations/${id}`),
  createOrganization: (data) => api.post('/superadmin/organizations', data),
  updateOrganization: (id, data) => api.patch(`/superadmin/organizations/${id}`, data),
  suspendOrganization: (id) => api.patch(`/superadmin/organizations/${id}/suspend`),
  activateOrganization: (id) => api.patch(`/superadmin/organizations/${id}/activate`),
  renewSubscription: (id, data) => api.patch(`/superadmin/organizations/${id}/renew-subscription`, data),
  // Linked Workspaces (docs/LINKED_WORKSPACES_PLAN.md) — DISABLED for now, feature
  // planned for later. Commented out, not removed, per explicit request.
  // setBillingParent: (id, billingParentOrganizationId) =>
  //   api.patch(`/superadmin/organizations/${id}/billing-parent`, { billingParentOrganizationId }),
  deleteOrganization: (id) => api.delete(`/superadmin/organizations/${id}`),
  createOrgAdmin: (orgId, data) => api.post(`/superadmin/organizations/${orgId}/create-admin`, data),

  // Super admin DIRECT LOGIN into an organization's admin panel —
  // issues the org admin's session token without their password
  loginAsAdmin: (orgId) =>
    api.post(`/superadmin/organizations/${orgId}/login-as-admin`, {}, { timeout: 60000 }),

  // Email verification OTP for org admin email
  sendOrgAdminOTP: (email) => api.post('/superadmin/send-org-admin-otp', { email }),
  verifyOrgAdminOTP: (email, otp) => api.post('/superadmin/verify-org-admin-otp', { email, otp }),

  // Plans
  getPlans: () => api.get('/superadmin/plans'),
  createPlan: (data) => api.post('/superadmin/plans', data),
  updatePlan: (id, data) => api.patch(`/superadmin/plans/${id}`, data),
  deletePlan: (id) => api.delete(`/superadmin/plans/${id}`),

  // Leads (Demo Requests) — legacy, currently unused by the live landing page
  getLeads: (params) => api.get('/leads/admin', { params }),
  getLead: (id) => api.get(`/leads/admin/${id}`),
  updateLeadStatus: (id, status) => api.patch(`/leads/admin/${id}/status`, { status }),

  // Website Leads — Contact, Book a Demo, and Connect (QR) form submissions
  getWebsiteLeads: (params) => api.get('/superadmin/website-leads', { params }),
  updateWebsiteLead: (id, data) => api.patch(`/superadmin/website-leads/${id}`, data),
  deleteWebsiteLead: (id) => api.delete(`/superadmin/website-leads/${id}`),

  // Demo Bookings (landing page /book-demo form)
  getDemoBookings: (params) => api.get('/superadmin/demo-bookings', { params }),
  getDemoBooking: (id) => api.get(`/superadmin/demo-bookings/${id}`),
  updateDemoBookingStatus: (id, status) => api.patch(`/superadmin/demo-bookings/${id}/status`, { status }),

  // Analytics
  getOverview: () => api.get('/superadmin/analytics/overview'),
  getRevenueStats: () => api.get('/superadmin/analytics/revenue'),

  // Admins
  getAdmins: (params) => api.get('/superadmin/admins', { params }),
  createAdmin: (data) => api.post('/superadmin/admins', data),
  updateAdmin: (id, data) => api.patch(`/superadmin/admins/${id}`, data),
  deleteAdmin: (id) => api.delete(`/superadmin/admins/${id}`),

  // Send custom email to org admins
  sendEmailToAdmins: (data) => api.post('/superadmin/admins/send-email', data, { timeout: 60000 }),

  // Support — ticket oversight (all organizations)
  getSupportTickets: (params) => api.get('/superadmin/support/tickets', { params }),
  getSupportTicketStats: () => api.get('/superadmin/support/tickets/stats'),
  getSupportTicket: (id) => api.get(`/superadmin/support/tickets/${id}`),
  // Active 'support'-role admins for the assignee dropdown — only needs
  // manage_support, unlike getAdmins() (manage_users)
  getAssignableSupportAdmins: () => api.get('/superadmin/support/assignable-admins'),
  followupSupportTicket: (id, data) => api.post(`/superadmin/support/tickets/${id}/followup`, data),
  // Same endpoint, multipart when a reply is sent together with files
  // (attach-then-send) instead of as a separate upload action
  followupSupportTicketWithFiles: (id, formData) =>
    api.post(`/superadmin/support/tickets/${id}/followup`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  // Attach files (screenshots, documents, images, …) to a ticket
  uploadTicketAttachments: (id, formData) =>
    api.post(`/superadmin/support/tickets/${id}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  updateSupportTicket: (id, data) => api.patch(`/superadmin/support/tickets/${id}`, data),

  // Ticket reassignment approval (a 'support'-role admin's assignee change
  // needs a super_admin's sign-off before it applies — see PATCH /support/tickets/:id)
  getReassignmentRequestSummary: () => api.get('/superadmin/support/reassignment-requests/summary'),
  getReassignmentRequests: (params) => api.get('/superadmin/support/reassignment-requests', { params }),
  approveReassignmentRequest: (id, data) => api.post(`/superadmin/support/reassignment-requests/${id}/approve`, data),
  rejectReassignmentRequest: (id, data) => api.post(`/superadmin/support/reassignment-requests/${id}/reject`, data),

  // Subscription Notification Testing
  sendTestSubscriptionEmail: (organizationId, daysLeft) =>
    api.post('/superadmin/subscription-notifications/test-email', { organizationId, daysLeft }),

  // Renewal Notifications (overdue renewal tasks escalated to managers)
  getRenewalNotifications: (params) => api.get('/superadmin/renewal-escalations', { params }),
  getRenewalNotificationSummary: () => api.get('/superadmin/renewal-escalations/summary'),
  markAllRenewalNotificationsRead: () => api.patch('/superadmin/renewal-escalations/mark-all-read'),

  // Renewal Performance Report (upcoming / pending / completed / missed renewals)
  getRenewalReport: () => api.get('/superadmin/renewal-report'),

  // Renewal Conversion Analytics (% of expiring subscriptions renewed)
  getRenewalConversion: (params) => api.get('/superadmin/renewal-conversion', { params }),

  // Renewal Agent Performance (salesperson renewal handling/conversion)
  getRenewalAgentPerformance: () => api.get('/superadmin/renewal-agent-performance'),

  // Renewal Loss Report (expired without renewal + recorded reasons)
  getRenewalLossReport: (params) => api.get('/superadmin/renewal-loss-report', { params }),
  recordRenewalLossReason: (data) => api.patch('/superadmin/renewal-loss-report/record-reason', data),

  // Subscription Expiry Report (all organizations as per subscription end date)
  getSubscriptionExpiryReport: (params) => api.get('/superadmin/subscription-expiry-report', { params }),

  // Ticket Resolution Time Analysis (avg / min / max time to resolve tickets)
  getTicketResolutionAnalysis: (params) => api.get('/superadmin/ticket-resolution-analysis', { params }),

  // First Response Time Analysis (avg / min / max time to first respond to new tickets)
  getFirstResponseAnalysis: (params) => api.get('/superadmin/first-response-analysis', { params }),

  // SLA Performance Report (compliance, breaches, tickets approaching deadline)
  getSlaPerformance: (params) => api.get('/superadmin/sla-performance', { params }),

  // Overdue Ticket Report (tickets past expected response / resolution time)
  getOverdueTickets: (params) => api.get('/superadmin/overdue-tickets', { params }),

  // Escalation Report (escalated follow-ups + renewals: reason, priority, assignee)
  getEscalationReport: (params) => api.get('/superadmin/escalation-report', { params }),

  // Reopened Ticket Report (tickets reopened after resolved/closed)
  getReopenedTickets: (params) => api.get('/superadmin/reopened-tickets', { params }),

  // Customer-wise Ticket Report (volume, status, resolution per customer)
  getCustomerTicketReport: (params) => api.get('/superadmin/customer-ticket-report', { params }),

  // Product-wise Ticket Report (tickets per product/service via ticket category)
  getProductTicketReport: (params) => api.get('/superadmin/product-ticket-report', { params }),

  // Ticket Trend Analysis (creation / resolution / pending trends per day)
  getTicketTrendAnalysis: (params) => api.get('/superadmin/ticket-trend-analysis', { params }),

  // Ticket Closure Rate (% of tickets resolved or closed)
  getTicketClosureRate: (params) => api.get('/superadmin/ticket-closure-rate', { params }),

  // Calendar settings — embedded Google Calendar ID + platform-wide feed URL
  getCalendarSettings: () => api.get('/superadmin/calendar-settings'),
  updateCalendarSettings: (googleCalendarId) =>
    api.put('/superadmin/calendar-settings', { googleCalendarId }),

  // AI Call Analysis model — Claude (default) / Ollama
  getAISettings: () => api.get('/superadmin/ai-settings'),
  updateAISettings: (data) => api.put('/superadmin/ai-settings', data),
  testAISettings: (data) => api.post('/superadmin/ai-settings/test', data),

  // Email Provider — SMTP / Brevo / Zepto, primary + fallback
  getEmailSettings: () => api.get('/superadmin/email-settings'),
  updateEmailSettings: (data) => api.put('/superadmin/email-settings', data),
  testEmailSettings: (data) => api.post('/superadmin/email-settings/test', data),
  // Company account (TeleValley's own org): demo bookings → leads, subscription digest
  getCompanyAccount: () => api.get('/superadmin/company-account'),
  updateCompanyAccount: (data) => api.put('/superadmin/company-account', data),
  sendCompanyAlerts: () => api.post('/superadmin/company-account/send-alerts'),
  copyDemoBookingsToCompany: () => api.post('/superadmin/company-account/copy-demo-bookings'),

  // Razorpay — Key ID/Secret, webhook secret, RazorpayX account number
  getRazorpaySettings: () => api.get('/superadmin/razorpay-settings'),
  updateRazorpaySettings: (data) => api.put('/superadmin/razorpay-settings', data),
  testRazorpaySettings: () => api.post('/superadmin/razorpay-settings/test'),

  // Online meetings (Google Meet integration) across all organizations
  getMeetings: (params) => api.get('/superadmin/meetings', { params }),
  createMeeting: (data) => api.post('/superadmin/meetings', data),
  updateMeeting: (id, data) => api.patch(`/superadmin/meetings/${id}`, data),

  // Auth
  login: (email, password) => api.post('/superadmin/auth/login', { email, password }),
  getMe: () => api.get('/superadmin/auth/me'),
  changePassword: (currentPassword, newPassword) =>
    api.post('/superadmin/auth/change-password', { currentPassword, newPassword }),
};

// ========================================
// GOOGLE SHEET INTEGRATION API
// ========================================

export const googleSheetAPI = {
  // Get all API keys for the organization
  getApiKeys: () => api.get('/google-sheet/api-keys'),

  // Generate new API key
  createApiKey: (data) => api.post('/google-sheet/api-keys', data),

  // Update API key
  updateApiKey: (id, data) => api.put(`/google-sheet/api-keys/${id}`, data),

  // Delete API key
  deleteApiKey: (id) => api.delete(`/google-sheet/api-keys/${id}`),

  // Get Apps Script code
  getScript: (apiKeyId) => api.get('/google-sheet/script', { params: { apiKeyId } }),

  // Test API key connection (public endpoint)
  testConnection: (apiKey) => api.get('/google-sheet/test', {
    headers: { 'X-API-Key': apiKey }
  }),
};

// ========================================
// API KEYS (Super Admin - Organization API Access)
// ========================================

export const apiKeysAPI = {
  // List API keys for an organization
  list: (orgId) => api.get(`/superadmin/organizations/${orgId}/api-keys`),

  // Create new API key
  create: (orgId, data) => api.post(`/superadmin/organizations/${orgId}/api-keys`, data),

  // Get single API key
  get: (orgId, keyId) => api.get(`/superadmin/organizations/${orgId}/api-keys/${keyId}`),

  // Update API key (scopes, name, status, IPs, rate limit)
  update: (orgId, keyId, data) => api.patch(`/superadmin/organizations/${orgId}/api-keys/${keyId}`, data),

  // Delete API key
  delete: (orgId, keyId) => api.delete(`/superadmin/organizations/${orgId}/api-keys/${keyId}`),

  // One-time reveal of signing secret
  revealSecret: (orgId, keyId) => api.get(`/superadmin/organizations/${orgId}/api-keys/${keyId}/reveal-secret`),

  // Regenerate signing secret
  regenerateSecret: (orgId, keyId) => api.post(`/superadmin/organizations/${orgId}/api-keys/${keyId}/regenerate-secret`),

  // Regenerate API key itself
  regenerateKey: (orgId, keyId) => api.post(`/superadmin/organizations/${orgId}/api-keys/${keyId}/regenerate-key`),
};

// ========================================
// ASSIGNMENT RULES API
// ========================================

export const assignmentRulesAPI = {
  // List all assignment rules for the organization
  list: () => api.get('/assignment-rules'),

  // Create a new assignment rule
  create: (data) => api.post('/assignment-rules', data),

  // Get a single assignment rule
  get: (id) => api.get(`/assignment-rules/${id}`),

  // Update an assignment rule
  update: (id, data) => api.patch(`/assignment-rules/${id}`, data),

  // Delete an assignment rule
  delete: (id) => api.delete(`/assignment-rules/${id}`),

  // Toggle rule active/inactive
  toggle: (id) => api.patch(`/assignment-rules/${id}/toggle`),

  // Reorder rule priorities
  reorder: (order) => api.post('/assignment-rules/reorder', { order }),

  // Get org auto-assign toggle status
  getAutoAssign: () => api.get('/assignment-rules/auto-assign'),

  // Toggle org auto-assign on/off
  toggleAutoAssign: (enabled) => api.patch('/assignment-rules/auto-assign', { enabled }),

  // Assign existing unassigned leads using the active rules / auto-assign
  applyToUnassigned: () => api.post('/assignment-rules/apply-unassigned'),
};

// ========================================
// RECORDING ACCESS API
// ========================================

export const recordingAccessAPI = {
  // Get recording access setting
  get: () => api.get('/telecalling/recording-access'),

  // Update recording access setting
  update: (recordingAccess) => api.patch('/telecalling/recording-access', { recordingAccess }),
};

// ========================================
// CALL ANALYSIS SETTINGS API (Settings → Calls)
// mode: 'manual' = pick specific calls to analyze;
//       'auto' = direct/automatic analysis, limited per day (limit set by Superadmin)
// ========================================

export const callAnalysisSettingsAPI = {
  // Get mode + superadmin-set daily limit + today's auto usage + business model
  get: () => api.get('/telecalling/call-analysis-settings'),

  // Switch the mode (admin can't change the daily limit)
  updateMode: (mode) => api.patch('/telecalling/call-analysis-settings', { mode }),

  // Set the business model — tells the AI how to identify and score calls
  updateBusinessModel: (businessModel) =>
    api.patch('/telecalling/call-analysis-settings', { businessModel }),

  // ── Transcription (Deepgram) API key — used by AI Call Analysis ──
  // When the old key expires, a super admin pastes the new key here and
  // call analysis continues with it immediately (no restart, no error).
  // Current key status { configured, masked, source, updatedAt }
  getApiKeyStatus: () => api.get('/call-analysis/api-key'),

  // Replace the (expired) key — validated against Deepgram before saving
  updateApiKey: (apiKey) => api.put('/call-analysis/api-key', { apiKey }),

  // Remove the custom key and restore the server default
  clearApiKey: () => api.delete('/call-analysis/api-key'),
};

// CUSTOM BUSINESS MODELS (Settings → Calls → Add Custom)
// Admin-defined business models — the AI scores calls against their guidance
export const customBusinessModelAPI = {
  // Add a new custom business model { label, description, guidance }
  create: (data) => api.post('/telecalling/call-analysis-custom-models', data),

  // Remove a custom business model by its key
  remove: (key) => api.delete(`/telecalling/call-analysis-custom-models/${key}`),
};

// ========================================
// PIPELINE STAGES API
// ========================================

export const pipelineAPI = {
  // Get all pipeline stages for the organization
  getAll: () => api.get('/pipeline-stages'),

  // Get single pipeline stage
  getById: (id) => api.get(`/pipeline-stages/${id}`),

  // Create new pipeline stage
  create: (data) => api.post('/pipeline-stages', data),

  // Update pipeline stage
  update: (id, data) => api.put(`/pipeline-stages/${id}`, data),

  // Delete pipeline stage
  delete: (id) => api.delete(`/pipeline-stages/${id}`),

  // Reorder pipeline stages
  reorder: (stages) => api.put('/pipeline-stages/reorder/bulk', { stages }),

  // Reset to default stages
  resetDefaults: () => api.post('/pipeline-stages/reset-defaults'),

  // Get stage statistics (leads count per stage)
  getStats: (params) => api.get('/pipeline-stages/stats/summary', { params }),
};

// ========================================
// PRODUCTS API
// ========================================

export const productsAPI = {
  // Get all products for the organization (optional filters: active, search)
  getAll: (params) => api.get('/products', { params }),

  // Get single product
  getById: (id) => api.get(`/products/${id}`),

  // Create new product
  create: (data) => api.post('/products', data),

  // Update product
  update: (id, data) => api.put(`/products/${id}`, data),

  // Delete product
  delete: (id) => api.delete(`/products/${id}`),
};

// ========================================
// SCRIPTS API (telecaller call scripts)
// ========================================

export const scriptsAPI = {
  // Get all scripts (optional filters: productId, active, search)
  getAll: (params) => api.get('/scripts', { params }),

  // Get single script
  getById: (id) => api.get(`/scripts/${id}`),

  // Create new script
  create: (data) => api.post('/scripts', data),

  // Update script
  update: (id, data) => api.put(`/scripts/${id}`, data),

  // Delete script
  delete: (id) => api.delete(`/scripts/${id}`),
};

// ========================================
// INTEGRATIONS API
// ========================================

export const integrationsAPI = {
  // Get all integrations
  getAll: (params) => api.get('/webhook-integrations', { params }),

  // Get single integration
  getById: (id) => api.get(`/webhook-integrations/${id}`),

  // Create integration
  create: (data) => api.post('/webhook-integrations', data),

  // Update integration
  update: (id, data) => api.put(`/webhook-integrations/${id}`, data),

  // Delete integration
  delete: (id) => api.delete(`/webhook-integrations/${id}`),

  // Enable integration
  enable: (id) => api.post(`/webhook-integrations/${id}/enable`),

  // Disable integration
  disable: (id) => api.post(`/webhook-integrations/${id}/disable`),

  // Test integration connection
  test: (id) => api.post(`/webhook-integrations/${id}/test`),

  // Regenerate webhook secret
  regenerateSecret: (id) => api.post(`/webhook-integrations/${id}/regenerate-secret`),

  // Reveal webhook secret key (admin only)
  revealSecret: (id) => api.get(`/webhook-integrations/${id}/reveal-secret`),

  // Get integration logs
  getLogs: (id, params) => api.get(`/webhook-integrations/${id}/logs`, { params }),

  // Get log details
  getLogDetails: (integrationId, logId) => api.get(`/webhook-integrations/${integrationId}/logs/${logId}`),

  // Get integration statistics
  getStats: (id, params) => api.get(`/webhook-integrations/${id}/stats`, { params }),

  // Retry failed log
  retryLog: (integrationId, logId) => api.post(`/webhook-integrations/${integrationId}/logs/${logId}/retry`),

  // Get dashboard statistics
  getDashboardStats: (params) => api.get('/webhook-integrations/dashboard/stats', { params }),
};

// ========================================
// PAYMENTS API (Razorpay)
// ========================================

export const paymentAPI = {
  // Create a Razorpay order for plan upgrade/renewal
  createOrder: (data) => api.post('/payments/create-order', data),

  // Verify Razorpay payment after checkout
  verifyPayment: (data) => api.post('/payments/verify', data),

  // Create a renewal order for expired subscription
  createRenewalOrder: (data) => api.post('/payments/renew', data),

  // Recurring auto-pay (Razorpay Subscriptions) — enable sets up a mandate
  // (returns a subscription_id for the Checkout popup), verify confirms the
  // authorization payment, cancel stops future auto-charges.
  enableAutopay: () => api.post('/payments/autopay/enable'),
  verifyAutopay: (data) => api.post('/payments/autopay/verify', data),
  cancelAutopay: () => api.post('/payments/autopay/cancel'),

  // Downgrade to free plan (schedules for end of period if subscription active)
  downgrade: () => api.post('/payments/downgrade'),

  // Cancel a pending plan downgrade
  cancelDowngrade: () => api.delete('/payments/downgrade'),

  // Get payment history
  getHistory: (params) => api.get('/payments/history', { params }),

  // Get invoice HTML for a subscription
  getInvoice: (subscriptionId) => `${api.defaults.baseURL}/payments/invoice/${subscriptionId}`,

  // Download invoice as PDF
  downloadInvoice: (subscriptionId) => `${api.defaults.baseURL}/payments/invoice/${subscriptionId}?format=pdf`,

  // Get all active plans for checkout
  getPlans: () => api.get('/payments/plans'),

  // Validate a promo code
  validatePromo: (data) => api.post('/payments/validate-promo', data),

  // Create a seat upgrade order (add users to existing subscription)
  upgradeSeats: (data) => api.post('/payments/upgrade-seats', data),

  // Schedule a seat downgrade (takes effect at next renewal)
  scheduleSeatDowngrade: (data) => api.post('/payments/schedule-seat-downgrade', data),

  // Cancel a pending seat downgrade
  cancelSeatDowngrade: () => api.delete('/payments/schedule-seat-downgrade'),

  // Switch billing cycle (e.g. monthly → yearly) — scheduled for next renewal, no immediate charge
  switchCycle: (data) => api.post('/payments/switch-cycle', data),

  // Cancel a pending billing cycle switch
  cancelCycleSwitch: () => api.delete('/payments/switch-cycle'),

  // Activate subscription for free (100% discount / zero-amount — bypasses Razorpay)
  activateFree: (data) => api.post('/payments/activate-free', data),
};

// ========================================
// REPORT SETTINGS API (Daily PDF Reports)
// ========================================

export const reportSettingsAPI = {
  // Get current org's report settings
  getSettings: () => api.get('/report-settings'),

  // Update report settings
  updateSettings: (data) => api.put('/report-settings', data),

  // Trigger an immediate report send (for testing)
  sendNow: () => api.post('/report-settings/send-now'),
};

// ========================================
// CALL TARGETS API
// ========================================

export const callTargetAPI = {
  // Get all telecallers with their targets & today's progress
  getTargets: () => api.get('/call-targets'),

  // Set target for a single telecaller
  setTarget: (userId, dailyCallTarget) => api.put(`/call-targets/${userId}`, { dailyCallTarget }),

  // Bulk set targets for multiple telecallers
  bulkSetTargets: (targets) => api.put('/call-targets/bulk', { targets }),

  // Telecaller: get own target & progress
  getMyProgress: () => api.get('/call-targets/my-progress'),
};

// ========================================
// NOTICES API (Marquee)
// ========================================

export const noticesAPI = {
  // Get active notices (public — no auth required)
  getActive: (params) => api.get('/notices/active', { params }),

  // SuperAdmin CRUD (auth required)
  getAll: (params) => api.get('/superadmin/notices', { params }),
  getById: (id) => api.get(`/superadmin/notices/${id}`),
  create: (data) => api.post('/superadmin/notices', data),
  update: (id, data) => api.put(`/superadmin/notices/${id}`, data),
  delete: (id) => api.delete(`/superadmin/notices/${id}`),
  toggle: (id) => api.patch(`/superadmin/notices/${id}/toggle`),
};

// ========================================
// PROMOCODE ADMIN API (SuperAdmin)
// ========================================

export const promoAdminAPI = {
  getAll: (params) => api.get('/superadmin/promocodes', { params }),
  create: (data) => api.post('/superadmin/promocodes', data),
  update: (id, data) => api.patch(`/superadmin/promocodes/${id}`, data),
  delete: (id) => api.delete(`/superadmin/promocodes/${id}`),
};

// ========================================
// CHANNEL PARTNER ADMIN API (SuperAdmin)
// ========================================

export const channelPartnerAdminAPI = {
  getAll: (params) => api.get('/superadmin/channel-partners', { params }),
  create: (data) => api.post('/superadmin/channel-partners', data),
  update: (id, data) => api.patch(`/superadmin/channel-partners/${id}`, data),
  delete: (id) => api.delete(`/superadmin/channel-partners/${id}`),
  getCommissions: (id, params) => api.get(`/superadmin/channel-partners/${id}/commissions`, { params }),
  markCommissionPaid: (commissionId, data) => api.patch(`/superadmin/channel-partners/commissions/${commissionId}/mark-paid`, data),
  payoutCommission: (commissionId) => api.post(`/superadmin/channel-partners/commissions/${commissionId}/payout`),

  // Autopay batch run time (platform-wide) — WHO/HOW OFTEN per partner is set on the partner itself
  getAutopaySchedule: () => api.get('/superadmin/channel-partners/autopay-schedule'),
  updateAutopaySchedule: (data) => api.put('/superadmin/channel-partners/autopay-schedule', data),

  // Partner page enquiries ("Become an Affiliate" / "Any Query" submissions from the landing page)
  getEnquiries: (params) => api.get('/superadmin/partner-enquiries', { params }),
  updateEnquiry: (id, data) => api.patch(`/superadmin/partner-enquiries/${id}`, data),
  deleteEnquiry: (id) => api.delete(`/superadmin/partner-enquiries/${id}`),

  // Self-signup KYC applications (fetch with kycStatus: 'pending' etc. via getAll's params)
  reviewKyc: (id, data) => api.patch(`/superadmin/channel-partners/${id}/kyc`, data),

  // Messages to a partner (shows on their dashboard's Notice Board)
  getMessages: (id) => api.get(`/superadmin/channel-partners/${id}/messages`),
  sendMessage: (id, data) => api.post(`/superadmin/channel-partners/${id}/message`, data),
  broadcastMessage: (data) => api.post('/superadmin/channel-partners/broadcast-message', data),

  // Partner meeting requests
  getPartnerMeetingRequests: (params) => api.get('/superadmin/partner-meeting-requests', { params }),
  updatePartnerMeetingRequest: (id, data) => api.patch(`/superadmin/partner-meeting-requests/${id}`, data),
};

// ========================================
// APP VERSIONS API (SuperAdmin)
// ========================================

export const appVersionAPI = {
  // List all app versions
  getVersions: (params) => api.get('/superadmin/app-versions', { params }),

  // Get latest active version
  getLatest: () => api.get('/superadmin/app-versions/latest'),

  // Upload a new APK version (multipart form data). The URL has no '/upload'
  // segment, so the interceptor's upload timeout doesn't apply — APKs can be up
  // to 200MB, which takes far longer than the 30s default on a normal uplink.
  uploadVersion: (formData) => api.post('/superadmin/app-versions', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 15 * 60 * 1000, // 15 minutes
  }),

  // Send update notification to all org admins (increased timeout for email sending)
  notifyVersion: (id) => api.post(`/superadmin/app-versions/${id}/notify`, {}, { timeout: 60000 }),

  // Delete a version
  deleteVersion: (id) => api.delete(`/superadmin/app-versions/${id}`),
};

// ========================================
// PLATFORM CONFIG API (SuperAdmin — branding / invoice settings)
// ========================================

export const platformConfigAPI = {
  // Get current platform config
  getConfig: () => api.get('/superadmin/platform-config'),

  // Update platform config (text fields)
  updateConfig: (data) => api.put('/superadmin/platform-config', data),

  // Upload logo (multipart form data)
  uploadLogo: (formData) => api.post('/superadmin/platform-config/logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),

  // Remove logo
  removeLogo: () => api.delete('/superadmin/platform-config/logo'),
};

// ========================================
// REFER & EARN API (org-admin)
// ========================================

export const referAndEarnAPI = {
  // Get (or lazily generate) this org's own shareable referral token
  getOrGenerateToken: () => api.post('/organizations/me/referral-token'),

  // Invite a prospective business by email — sends them the referral link
  sendInvite: (email) => api.post('/organizations/me/referral-invite', { email }),

  // This org's coin transaction history (earned + redeemed)
  getCoinTransactions: (params) => api.get('/organizations/me/coin-transactions', { params }),
};

// ========================================
// AUDIT LOGS API
// Normal admin → organization-scoped logs (/audit-logs)
// Superadmin   → logs across all organizations (/superadmin/audit-logs)
// ========================================

export const auditLogsAPI = {
  // Normal admin endpoints (admin_auth_token)
  list: (params) => api.get('/audit-logs', { params }),
  stats: (params) => api.get('/audit-logs/stats', { params }),

  // Superadmin endpoints (superadmin_token)
  superAdminList: (params) => api.get('/superadmin/audit-logs', { params }),
  superAdminStats: (params) => api.get('/superadmin/audit-logs/stats', { params }),
};

export default api;